import { evaluate } from "./evaluator";
import { Parser } from "./parser";
import { Tokenizer } from "./tokenizer";

/**
 * Compile a math expression string into a reusable function.
 *
 * The expression is tokenized and parsed once. The returned function
 * applies the compiled AST to a new `y` value on each call — O(expression
 * complexity) with no string parsing overhead per data point.
 *
 * Only the variable `y` is supported. Built-in math functions (abs, sqrt,
 * pow, sin, cos, tan, log, exp, min, max, round, floor, ceil, rad, deg)
 * and constants (PI, E) are pre-populated.
 *
 * Throws if the expression fails to parse or references an unknown identifier.
 *
 * @example
 * const f = compileFormula("y * 100 + 1");
 * f(2); // → 201
 */
export function compileFormula(expr: string): (y: number) => number {
  const tokens = new Tokenizer(expr).tokenize();
  const ast = Parser.parse(tokens);
  // Dry-run with y=1 to catch errors at compile time.
  evaluate(ast, { y: 1 });
  return (y: number) => evaluate(ast, { y });
}

/**
 * Validate a math expression string without throwing.
 *
 * Returns `{ valid: true }` if the expression compiles successfully,
 * or `{ valid: false, error: string }` if it does not.
 */
export function validateFormula(
  expr: string,
): { valid: true } | { valid: false; error: string } {
  try {
    compileFormula(expr);
    return { valid: true };
  } catch (e) {
    return { valid: false, error: (e as Error).message };
  }
}
